import { useMemo, useState } from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { captureToofiAnalytics } from "@/lib/toofi-analytics";
import { cn } from "@/lib/utils";
import type { ReaderAiThread } from "@/types/agentic-chat";
import type { ToofiShellSidebarView } from "@/components/ui/reader-ai/types";
import { ToofiSidebarSearch } from "@/components/ui/reader-ai/toofi-sidebar-search";
import { ToofiSidebarThreadItem } from "@/components/ui/reader-ai/toofi-sidebar-thread-item";
import { Archive, Clock3, Layers3, MessageSquareText, PenSquare } from "lucide-react";

type ToofiSidebarProps = {
  threads: ReaderAiThread[];
  activeThreadId: string | null;
  artifactCount: number;
  currentView: ToofiShellSidebarView;
  compact?: boolean;
  showArchived?: boolean;
  showScopeLabel?: boolean;
  showHeaderActions?: boolean;
  showViewSwitch?: boolean;
  className?: string;
  onViewChange: (view: ToofiShellSidebarView) => void;
  onNewChat: () => void;
  onStartTemporaryChat?: () => void;
  onToggleArchived?: () => void;
  onSelectThread: (threadId: string) => void;
  onRenameThread: (threadId: string, title: string) => void;
  onDeleteThread: (threadId: string) => void;
  onPinThread?: (threadId: string) => void;
  onUnpinThread?: (threadId: string) => void;
  onArchiveThread?: (threadId: string) => void;
  onRestoreThread?: (threadId: string) => void;
  onStartSimilarChat?: (threadId: string) => void;
  onSetThreadMemoryMode?: (threadId: string, mode: "default" | "temporary") => void;
};

const VIEW_OPTIONS: Array<{ value: ToofiShellSidebarView; label: string; icon: typeof MessageSquareText }> = [
  { value: "chats", label: "Chats", icon: MessageSquareText },
  { value: "artifacts", label: "Artifacts", icon: Layers3 },
];

export function ToofiSidebar({
  threads,
  activeThreadId,
  artifactCount,
  currentView,
  compact = false,
  showArchived,
  showScopeLabel = false,
  showHeaderActions = true,
  showViewSwitch = true,
  className,
  onViewChange,
  onNewChat,
  onStartTemporaryChat,
  onToggleArchived,
  onSelectThread,
  onRenameThread,
  onDeleteThread,
  onPinThread,
  onUnpinThread,
  onArchiveThread,
  onRestoreThread,
  onStartSimilarChat,
  onSetThreadMemoryMode,
}: ToofiSidebarProps) {
  const [internalShowArchived, setInternalShowArchived] = useState(false);
  const [threadSearch, setThreadSearch] = useState("");
  const [renamingThread, setRenamingThread] = useState<ReaderAiThread | null>(null);
  const [renameValue, setRenameValue] = useState("");
  const [deletingThread, setDeletingThread] = useState<ReaderAiThread | null>(null);
  const archivedOpen = showArchived ?? internalShowArchived;

  const filteredThreads = useMemo(() => {
    const query = threadSearch.trim().toLowerCase();
    const sorted = threads.slice().sort((left, right) => {
      if (Boolean(left.pinned) !== Boolean(right.pinned)) return left.pinned ? -1 : 1;
      return right.updatedAt - left.updatedAt;
    });
    const filteredByBucket = sorted.filter((thread) => (archivedOpen ? Boolean(thread.archived) : !thread.archived));
    if (!query) return filteredByBucket;
    return filteredByBucket.filter((thread) => thread.title.toLowerCase().includes(query));
  }, [archivedOpen, threadSearch, threads]);

  const showingArtifactsPanel = showViewSwitch && currentView === "artifacts";
  const showSearch = !showingArtifactsPanel && (threads.length > 5 || threadSearch.trim().length > 0);
  const handleArchivedToggle = () => {
    const next = !archivedOpen;
    captureToofiAnalytics("toofi_thread_filter_changed", {
      filter: next ? "archived" : "active",
      source: compact ? "mobile_drawer" : "sidebar",
    });
    if (typeof showArchived !== "boolean") {
      setInternalShowArchived(next);
    }
    onToggleArchived?.();
    if (currentView !== "chats") {
      onViewChange("chats");
    }
  };

  return (
    <>
      <div className={cn("relative flex h-full min-h-0 flex-col overflow-hidden", className)}>
        {showHeaderActions ? (
          <div className={cn("border-b border-white/70", compact ? "px-3 pb-3 pt-2.5" : "px-3 pb-3.5 pt-3 lg:px-3.5 lg:pb-4")}>
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <Button
                type="button"
                size="icon"
                title="New chat"
                aria-label="Start a new chat"
                className="h-9 w-9 rounded-full border border-blue-500/80 bg-blue-600 text-white shadow-[0_14px_30px_rgba(37,99,235,0.18)] transition-all duration-200 hover:-translate-y-0.5 hover:bg-blue-600/95"
                onClick={() => {
                  captureToofiAnalytics("toofi_new_chat_clicked", { source: compact ? "mobile_drawer" : "sidebar" });
                  onViewChange("chats");
                  onNewChat();
                }}
              >
                <PenSquare className="h-4 w-4" />
                <span className="sr-only">New chat</span>
              </Button>
              {onStartTemporaryChat ? (
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  title="Temporary chat"
                  aria-label="Start a temporary chat"
                  className="h-9 w-9 rounded-full border border-slate-200/80 bg-white/90 text-slate-600 shadow-[0_10px_24px_rgba(148,163,184,0.10)] transition-all duration-200 hover:-translate-y-0.5 hover:bg-white hover:text-slate-900"
                  onClick={() => {
                    captureToofiAnalytics("toofi_temporary_chat_clicked", { source: compact ? "mobile_drawer" : "sidebar" });
                    onViewChange("chats");
                    onStartTemporaryChat();
                  }}
                >
                  <Clock3 className="h-4 w-4 text-amber-500" />
                  <span className="sr-only">Temporary chat</span>
                </Button>
              ) : null}
            </div>
            <Button
              type="button"
              size="icon"
              variant="ghost"
              title={archivedOpen ? "Show active chats" : "Show archived chats"}
              aria-label={archivedOpen ? "Show active chats" : "Show archived chats"}
              className={cn(
                "h-9 w-9 rounded-full border shadow-[0_10px_24px_rgba(148,163,184,0.10)] transition-all duration-200",
                archivedOpen
                  ? "border-blue-200/80 bg-blue-50 text-blue-700 hover:-translate-y-0.5 hover:bg-blue-100/90"
                  : "border-slate-200/80 bg-white/90 text-slate-500 hover:-translate-y-0.5 hover:bg-white hover:text-slate-900",
              )}
              onClick={handleArchivedToggle}
            >
              <Archive className="h-4 w-4" />
              <span className="sr-only">{archivedOpen ? "Show active chats" : "Show archived chats"}</span>
            </Button>
          </div>
          </div>
        ) : null}

        <div className={cn("min-h-0 flex-1", compact ? "px-3 py-3" : showHeaderActions ? "px-3 py-3.5 lg:px-3.5 lg:py-4" : "px-3 py-3 lg:px-3.5 lg:py-3.5")}>
          {!showingArtifactsPanel ? (
            <div className="flex h-full min-h-0 flex-col gap-2">
              {showSearch ? <ToofiSidebarSearch value={threadSearch} onChange={setThreadSearch} compact={compact} /> : null}
              <div className="flex items-center justify-between gap-2 px-0.5">
                <p className="text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-400">
                  {archivedOpen ? "Archived chats" : "Chats"}
                </p>
              </div>
              <ScrollArea className="min-h-0 flex-1 [&>[data-orientation='horizontal']]:hidden [&>[data-orientation='vertical']]:hidden">
                <div className="space-y-2 pb-2 pr-0.5">
                  {filteredThreads.length ? (
                    filteredThreads.map((thread) => (
                      <ToofiSidebarThreadItem
                        key={thread.id}
                        thread={thread}
                        active={thread.id === activeThreadId}
                        compact={compact}
                        showScopeLabel={showScopeLabel}
                        onSelect={onSelectThread}
                        onRequestRename={(nextThread) => {
                          setRenamingThread(nextThread);
                          setRenameValue(nextThread.title);
                        }}
                        onRequestDelete={setDeletingThread}
                        onPinThread={onPinThread}
                        onUnpinThread={onUnpinThread}
                        onArchiveThread={onArchiveThread}
                        onRestoreThread={onRestoreThread}
                        onStartSimilarChat={onStartSimilarChat}
                        onSetThreadMemoryMode={onSetThreadMemoryMode}
                      />
                    ))
                  ) : (
                    <div className="rounded-[1rem] border border-slate-200/75 bg-white/78 px-3 py-3 text-[12px] text-slate-500 shadow-[inset_0_1px_0_rgba(255,255,255,0.7)]">
                      {threads.length
                        ? archivedOpen
                          ? "No archived chats yet."
                          : "No chats match this search."
                        : "No chats yet. Start a new one when you need Toofi."}
                    </div>
                  )}
                </div>
              </ScrollArea>
            </div>
          ) : (
            <div className="flex h-full min-h-0 flex-col rounded-[1.15rem] border border-white/75 bg-[linear-gradient(180deg,rgba(255,255,255,0.84),rgba(248,250,252,0.7))] px-3.5 py-3.5 shadow-[inset_0_1px_0_rgba(255,255,255,0.7),0_18px_36px_rgba(148,163,184,0.08)]">
              <div className="flex items-center justify-between gap-2">
                <p className="text-[11px] font-semibold text-slate-800">Saved artifacts</p>
                <div className="inline-flex items-center rounded-full border border-slate-200/80 bg-white/90 px-2.5 py-1 text-[10px] font-semibold text-slate-500">
                  {artifactCount}
                </div>
              </div>
              <p className="mt-2 text-[11px] leading-5 text-slate-500">Open saved plans, notes, and quizzes in the main panel.</p>
            </div>
          )}
        </div>

        {showViewSwitch ? (
          <div className={cn("border-t border-white/70", compact ? "px-3 pb-3.5 pt-2.5" : "px-3 pb-3.5 pt-3 lg:px-3.5")}>
            <div className="inline-flex w-full rounded-full border border-slate-200/80 bg-white/88 p-0.5 shadow-[inset_0_1px_0_rgba(255,255,255,0.72),0_8px_18px_rgba(148,163,184,0.08)]">
              {VIEW_OPTIONS.map((option) => {
                const Icon = option.icon;
                return (
                  <button
                    key={option.value}
                    type="button"
                    className={cn(
                      "flex h-8 flex-1 items-center justify-center gap-1.5 rounded-full px-2.5 text-[10px] font-semibold transition-all duration-200 active:scale-[0.98]",
                      currentView === option.value
                        ? "bg-blue-600 text-white shadow-[0_10px_22px_rgba(37,99,235,0.18)]"
                        : "bg-transparent text-slate-500 hover:bg-blue-50/85 hover:text-slate-800",
                    )}
                    onClick={() => {
                      captureToofiAnalytics("toofi_sidebar_view_changed", {
                        view: option.value,
                        source: compact ? "mobile_drawer" : "sidebar",
                      });
                      if (option.value === "artifacts") {
                        captureToofiAnalytics("toofi_artifacts_view_opened", {
                          source: compact ? "mobile_drawer" : "sidebar",
                        });
                      }
                      onViewChange(option.value);
                    }}
                  >
                    <Icon className="h-3.5 w-3.5" />
                    {option.label}
                  </button>
                );
              })}
            </div>
          </div>
        ) : null}
      </div>

      <Dialog
        open={Boolean(renamingThread)}
        onOpenChange={(open) => {
          if (!open) {
            setRenamingThread(null);
            setRenameValue("");
          }
        }}
      >
        <DialogContent className="w-[calc(100vw-1.5rem)] max-w-sm rounded-[1.5rem] border border-slate-200/70 bg-white/95 backdrop-blur-xl">
          <DialogHeader>
            <DialogTitle>Rename chat</DialogTitle>
            <DialogDescription className="text-sm text-slate-500">
              Give this Toofi thread a clearer name so it is easier to find later.
            </DialogDescription>
          </DialogHeader>
          <Input
            value={renameValue}
            onChange={(event) => setRenameValue(event.target.value.slice(0, 80))}
            placeholder="Chat title"
            className="rounded-[1rem] border-slate-200/70 bg-white/90"
            autoFocus
          />
          <DialogFooter className="flex-row justify-end gap-2">
            <Button
              variant="ghost"
              onClick={() => {
                setRenamingThread(null);
                setRenameValue("");
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={() => {
                if (!renamingThread) return;
                const title = renameValue.trim();
                if (!title) return;
                onRenameThread(renamingThread.id, title);
                setRenamingThread(null);
                setRenameValue("");
              }}
              disabled={!renameValue.trim()}
            >
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={Boolean(deletingThread)}
        onOpenChange={(open) => {
          if (!open) setDeletingThread(null);
        }}
      >
        <AlertDialogContent className="w-[calc(100vw-1.5rem)] max-w-sm rounded-[1.5rem] border border-slate-200/70 bg-white/95 backdrop-blur-xl">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete chat?</AlertDialogTitle>
            <AlertDialogDescription>
              This removes the thread and its messages from this Toofi workspace.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-rose-600 text-white hover:bg-rose-700"
              onClick={() => {
                if (!deletingThread) return;
                onDeleteThread(deletingThread.id);
                setDeletingThread(null);
              }}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
